import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Package } from "lucide-react";

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          api.get("/products"),
          api.get("/categories")
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (e) {
        console.error("Error fetching products:", e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const category = searchParams.get("category");
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.short_description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categoriesWithProducts = useMemo(
    () => categories.filter((category) =>
      products.some((product) => product.category === category.slug)
    ),
    [categories, products]
  );

  useEffect(() => {
    if (
      !loading &&
      selectedCategory &&
      !categoriesWithProducts.some((category) => category.slug === selectedCategory)
    ) {
      setSelectedCategory("");
      setSearchParams({});
    }
  }, [categoriesWithProducts, loading, selectedCategory, setSearchParams]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow opacity-30" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
              Product Catalog
            </span>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              All Scripts
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover our collection of premium aviation scripts for your FiveM server
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-96">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search scripts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="pl-12 bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg h-12"
                />
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCategory === "" ? "default" : "outline"}
                  onClick={() => handleCategoryChange("")}
                  data-testid="filter-all"
                  className={`rounded-lg text-xs uppercase tracking-wider ${
                    selectedCategory === ""
                      ? "bg-primary text-primary-foreground"
                      : "bg-transparent border-slate-700 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  All
                </Button>
                {categoriesWithProducts.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.slug ? "default" : "outline"}
                    onClick={() => handleCategoryChange(category.slug)}
                    data-testid={`filter-${category.slug}`}
                    className={`rounded-lg text-xs uppercase tracking-wider ${
                      selectedCategory === category.slug
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent border-slate-700 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {category.name}
                  </Button>
                ))}
                {selectedCategory && (
                  <Button
                    variant="ghost"
                    onClick={() => handleCategoryChange("")}
                    className="rounded-lg text-xs text-destructive hover:text-destructive"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear Filter
                  </Button>
                )}
              </div>
            </div>

            {/* Results count */}
            <div className="mt-6 text-sm text-muted-foreground">
              <span className="text-foreground font-bold">{filteredProducts.length}</span>{" "}
              {filteredProducts.length === 1 ? "product" : "products"} found
              {selectedCategory && (
                <span> in <span className="text-primary">{selectedCategory}</span> category</span>
              )}
            </div>
          </motion.div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-slate-900/40 border border-slate-800 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div
              className={
                filteredProducts.length === 1
                  ? "mx-auto grid max-w-4xl grid-cols-1 gap-6"
                  : "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
              }
              data-testid="products-grid"
            >
              {filteredProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  wide={filteredProducts.length === 1}
                />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="empty-state py-24"
            >
              <Package className="w-16 h-16 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold mb-2">No Products Found</h3>
              <p className="text-muted-foreground mb-6">
                No products match your search criteria.
              </p>
              <Button
                onClick={() => {
                  setSearchQuery("");
                  handleCategoryChange("");
                }}
                variant="outline"
                className="rounded-lg border-primary/30 text-primary"
              >
                Clear Filters
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
